/* eslint-disable react/jsx-props-no-spreading -- the drag handlers of an hour are a set of event props */
import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { platformMeta } from '../platformMeta';
import type { CalendarDay } from './calendarDates';
import { CalendarDayContent } from './calendarDayModel';
import type { CalendarGridProps } from './calendarGridTypes';
import { CalendarPostCard } from './CalendarPostCard';
import { formatPostTime } from './calendarPostView';
import { PlatformTile } from './PlatformTile';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WeekColumnProps extends Omit<CalendarGridProps, 'days' | 'contentByDay'> {
  day: CalendarDay;
  content: CalendarDayContent;
  hours: number[];
  showFreeSlots: boolean;
}

/**
 * One day of the Week view (design a2): one drop zone per hour. A drop sets the day and the hour
 * (`useDragMove` rounds to 15 minutes). The free slots are dashed ghosts at the best time of each
 * channel with no post that day; a click opens the Composer there.
 */
const WeekColumnComponent: React.FC<WeekColumnProps> = ({ day, content, hours, showFreeSlots, ...grid }) => {
  const intl = useIntl();
  const { drag, songs, onOpenPost, slotsFor, onAdd, selectedUuid } = grid;
  const slots = useMemo(
    () => (showFreeSlots ? slotsFor(day.start, content.posts) : []),
    [content.posts, day.start, showFreeSlots, slotsFor]
  );

  return (
    <div className={clsx(styles.wcol, day.isToday && styles.wcolToday)} data-testid={`week-day-${day.key}`}>
      {hours.map((hour) => {
        const posts = content.posts.filter((post) => new Date(post.scheduledAt).getHours() === hour);
        const ghosts = slots.filter((slot) => slot.at.getHours() === hour);
        const isTarget = drag.isHolding && drag.targetDayKey === day.key;
        return (
          <div
            key={hour}
            className={clsx(
              styles.whour,
              drag.isHolding && day.isPast && styles.dcNoDrop,
              isTarget && !day.isPast && styles.whourTarget
            )}
            data-testid={`week-hour-${day.key}-${hour}`}
            {...drag.dayHandlers(day.key, hour * 60)}
          >
            {posts.map((post) => (
              <CalendarPostCard
                key={post.uuid}
                post={post}
                song={post.catalogItemUuid === null ? undefined : songs.get(post.catalogItemUuid)}
                drag={drag}
                isSelected={selectedUuid === post.uuid}
                onOpen={onOpenPost}
              />
            ))}
            {ghosts.map((slot) => (
              <button
                key={slot.platform}
                type="button"
                className={styles.slot}
                onClick={() => onAdd(day.start, slot)}
                aria-label={intl.formatMessage(
                  { id: 'app.sml.promote.calendar.slot.label', defaultMessage: 'Free slot: {platform}, {time}' },
                  { platform: platformMeta[slot.platform].name, time: formatPostTime(intl.formatTime, slot.at) }
                )}
              >
                <PlatformTile platform={slot.platform} />
                <span>{formatPostTime(intl.formatTime, slot.at)}</span>
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export const WeekColumn = TypedMemo(WeekColumnComponent);
